/**
 * Single source of truth for the deck's prose + view-models (successor of
 * wealthDriver.ts). SIPS/planscout-api send raw numbers; this file maps them
 * to everything the 7 acts render. Every builder takes the optional
 * enrichment block and degrades gracefully when it's absent (older reports).
 */

import type {
  AdvisorMeta,
  AlignmentSeriesPoint,
  EndOfPlanAccountMix,
  EnrichedPerson,
  RawWealthDriver,
  ReportEnrichment,
  RothConversionEntry,
  TaxSeriesPoint,
  WebReportPayload,
} from '../types/webReport';
import { fmtMoney, fmtMoneyFull } from '../deck/theme';
import { buildSsBenefits } from '../utils/chartApprox';

/* ────────────────────────── categories / framing ────────────────────────── */

export type Framing = 'delta' | 'divergence' | 'beforeafter' | 'generic';
export type CategoryKey = 'investment-timing' | 'tax' | 'social-security' | 'legacy' | 'other';
export type OptIcon = 'shield' | 'trend' | 'repeat' | 'target';

export function categoryFor(driver: RawWealthDriver): CategoryKey {
  const t = (driver.type || '').toLowerCase();
  if (t.includes('asset alignment') || t.includes('timing')) return 'investment-timing';
  if (t.includes('tax efficiency') || t.includes('roth')) return 'tax';
  if (t.includes('social security') || t.includes('pension') || t.includes('spending'))
    return 'social-security';
  if (t.includes('legacy') || t.includes('transfer')) return 'legacy';
  return 'other';
}

const FRAMING_FOR: Record<CategoryKey, Framing> = {
  'social-security': 'delta',
  'investment-timing': 'divergence',
  tax: 'beforeafter',
  legacy: 'generic',
  other: 'generic',
};

const ICON_FOR: Record<CategoryKey, OptIcon> = {
  'social-security': 'shield',
  'investment-timing': 'trend',
  tax: 'repeat',
  legacy: 'target',
  other: 'target',
};

/* ────────────────────────────── optimization VM ───────────────────────────── */

export interface SsPersonVM {
  name: string;
  claimAge: number;
  benefit: number;
  changed: boolean;
  note?: string;
}

export interface BucketVM {
  name: string;
  years: string;
  amount: number;
  note: string;
  colorKey: 'teal' | 'blue' | 'navy' | 'gray';
}

export interface RothVM {
  schedule: RothConversionEntry[];
  total: number;
  firstYear: number | null;
  lastYear: number | null;
  taxSavings: number;
}

export interface OptimizationVM {
  key: string; // scenarioId — the tracked cardId
  category: CategoryKey;
  framing: Framing;
  icon: OptIcon;
  title: string;
  blurb: string;
  valueAdded: number;
  valueAddedLabel: string;
  strategyLabel: string;
  strategyTitle: string;
  strategyBody: string;
  whyLabel: string;
  whyBody: string;
  /** delta framing (Social Security) */
  ss?: { before: SsPersonVM[]; after: SsPersonVM[]; leverLabel: string };
  /** divergence framing (Align) */
  buckets?: BucketVM[];
  /** divergence framing — REAL per-year series (schema v8; absent on older reports) */
  alignmentSeries?: AlignmentSeriesPoint[];
  /** beforeafter framing (Roth) */
  roth?: RothVM;
  /** beforeafter framing — REAL cumulative-tax series (schema v8) */
  taxSeries?: TaxSeriesPoint[];
  /** beforeafter framing — REAL end-of-plan account mix (schema v8) */
  endOfPlanAccountMix?: EndOfPlanAccountMix;
  /** beforeafter framing — REAL baseline end-of-plan account mix (schema v9) */
  baselineEndOfPlanAccountMix?: EndOfPlanAccountMix;
}

function summarizeRoth(entries: RothConversionEntry[] | undefined): RothVM | null {
  const filtered = (entries || []).filter((e) => e.amount > 0);
  if (filtered.length === 0) return null;
  return {
    schedule: filtered,
    total: filtered.reduce((s, e) => s + e.amount, 0),
    firstYear: filtered[0].year,
    lastYear: filtered[filtered.length - 1].year,
    taxSavings: 0, // filled by caller from the driver
  };
}

function buildSsVM(
  driver: RawWealthDriver,
  people: EnrichedPerson[]
): OptimizationVM['ss'] | undefined {
  // Optimized (after = SC03) claim ages, plus — since payload schema v7 — the
  // as-is (before = SC01) claim ages and the REAL annual benefit dollars at each
  // claim age. When the real after-benefit is present we render the true before/
  // after figures; otherwise we fall back to the FRA-based approximation, with
  // "before" pinned to age 67 (older reports predating the benefit fields).
  const afterAges = [driver.client1SocialSecurityAge, driver.client2SocialSecurityAge];
  const beforeAgesReal = [driver.client1SocialSecurityAgeBefore, driver.client2SocialSecurityAgeBefore];
  const benefitsBeforeReal = [driver.client1SocialSecurityBenefitBefore, driver.client2SocialSecurityBenefitBefore];
  const benefitsAfterReal = [driver.client1SocialSecurityBenefitAfter, driver.client2SocialSecurityBenefitAfter];
  const rows: { before: SsPersonVM; after: SsPersonVM }[] = [];

  people.forEach((p, i) => {
    const fraAmount = p.socialSecurity.amount || 0;
    const realAfter = benefitsAfterReal[i];
    const hasReal = typeof realAfter === 'number' && realAfter > 0;
    if (!p.firstName || (fraAmount <= 0 && !hasReal)) return;

    if (p.socialSecurity.claimingNow) {
      const age = p.age ?? 67;
      rows.push({
        before: { name: p.firstName, claimAge: age, benefit: fraAmount, changed: false, note: 'claiming now' },
        after: { name: p.firstName, claimAge: age, benefit: fraAmount, changed: false, note: 'claiming now' },
      });
      return;
    }

    const optAfterAge = afterAges[i];
    const optBeforeAge = beforeAgesReal[i];

    let beforeAge: number;
    let afterAge: number;
    let beforeBenefit: number;
    let afterBenefit: number;

    if (hasReal) {
      const afterVal = realAfter as number;
      afterAge = optAfterAge && optAfterAge > 0 ? optAfterAge : (optBeforeAge || 67);
      beforeAge = optBeforeAge && optBeforeAge > 0 ? optBeforeAge : afterAge;
      afterBenefit = afterVal;
      const rb = benefitsBeforeReal[i];
      beforeBenefit = typeof rb === 'number' && rb > 0 ? rb : afterVal;
    } else {
      beforeAge = 67;
      afterAge = optAfterAge && optAfterAge > 0 ? optAfterAge : beforeAge;
      const approx = buildSsBenefits({ fraAmount, claimAgeBefore: beforeAge, claimAgeAfter: afterAge });
      beforeBenefit = approx.before;
      afterBenefit = approx.after;
    }

    const changed = afterAge !== beforeAge || afterBenefit !== beforeBenefit;
    rows.push({
      before: { name: p.firstName, claimAge: beforeAge, benefit: beforeBenefit, changed },
      after: { name: p.firstName, claimAge: afterAge, benefit: afterBenefit, changed },
    });
  });

  if (rows.length === 0) return undefined;
  const changedRow = rows.find((r) => r.after.changed);
  return {
    before: rows.map((r) => r.before),
    after: rows.map((r) => r.after),
    leverLabel: changedRow ? `${changedRow.after.name}'s annual benefit` : 'Annual benefit',
  };
}

function buildBuckets(driver: RawWealthDriver, horizonYears: number): BucketVM[] | undefined {
  const short = driver.bucketShort || 0;
  const medium = driver.bucketMedium || 0;
  const long = driver.bucketLong || 0;
  const disc = driver.bucketDisc || 0;
  if (short + medium + long + disc <= 0) return undefined;
  const shortEnd = driver.bucketShortEndYear || 4;
  const mediumEnd = driver.bucketMediumEndYear || 10;
  return [
    { name: 'Short-Term', years: `1–${shortEnd} yrs`, amount: short, note: 'Capital preservation', colorKey: 'teal' },
    { name: 'Medium-Term', years: `${shortEnd + 1}–${mediumEnd} yrs`, amount: medium, note: 'Moderate growth', colorKey: 'blue' },
    { name: 'Long-Term', years: `${mediumEnd + 1}–${horizonYears} yrs`, amount: long, note: 'Growth-oriented', colorKey: 'navy' },
    { name: 'Discretionary', years: 'Flexible', amount: disc, note: 'Legacy & priorities', colorKey: 'gray' },
  ].filter((b) => b.amount > 0) as BucketVM[];
}

const OPT_COPY: Record<CategoryKey, Pick<OptimizationVM, 'title' | 'strategyLabel' | 'whyLabel'>> = {
  'social-security': { title: 'Social Security Optimization', strategyLabel: 'The strategy', whyLabel: 'Why it works' },
  'investment-timing': { title: 'Align Investments to Time Horizons', strategyLabel: 'The strategy', whyLabel: 'Why it works' },
  tax: { title: 'Roth IRA Conversion', strategyLabel: 'Conversion strategy', whyLabel: 'Why it works' },
  legacy: { title: 'Legacy Strategy', strategyLabel: 'The strategy', whyLabel: 'Why it works' },
  other: { title: 'Planning Strategy', strategyLabel: 'The strategy', whyLabel: 'Why it works' },
};

export function buildOptimizations(
  payload: WebReportPayload,
  enrichment: ReportEnrichment | null
): OptimizationVM[] {
  const people = enrichment?.people || [];
  const horizon = payload.client.planHorizonYears || 30;

  return (payload.wealthDrivers || [])
    // Roth cards require a REAL conversion schedule: SIPS's SC06.01 bucket
    // strategy can leave a positive tax driver with zero surviving conversions —
    // allocation value, not a Roth recommendation. Matches the advisor dashboard
    // (getRealRothOpportunity in planOptimizationCards.ts).
    .filter((d) => categoryFor(d) !== 'tax' || (d.rothConversions || []).some((e) => e.amount > 0))
    .filter((d) => (d.wealthAdded || 0) > 0)
    .sort((a, b) => (b.wealthAdded || 0) - (a.wealthAdded || 0))
    .map((driver) => {
      const category = categoryFor(driver);
      const framing = FRAMING_FOR[category];
      const copy = OPT_COPY[category];
      // Tax/Roth card displays the full client benefit (tax savings + growth) —
      // newer payloads set wealthAdded to the growth component alone (2026-07
      // marginal-delta fix), which understates the story. Card existence and
      // ordering still key off wealthAdded (filter/sort above). Mirrors the
      // dashboard's planOptimizationCards.ts.
      const rothComponentSum =
        category === 'tax' && (driver.taxSavings != null || driver.growthImpact != null)
          ? (driver.taxSavings ?? 0) + (driver.growthImpact ?? 0)
          : null;
      const displayValue =
        rothComponentSum != null && rothComponentSum > 0 ? rothComponentSum : driver.wealthAdded;
      const value = fmtMoney(displayValue);

      const vm: OptimizationVM = {
        key: driver.scenarioId || driver.type,
        category,
        framing,
        icon: ICON_FOR[category],
        title: copy.title,
        blurb: '',
        valueAdded: displayValue,
        valueAddedLabel: `+${value}`,
        strategyLabel: copy.strategyLabel,
        strategyTitle: '',
        strategyBody: '',
        whyLabel: copy.whyLabel,
        whyBody: '',
      };

      if (category === 'social-security') {
        vm.ss = buildSsVM(driver, people);
        const changed = vm.ss?.after.find((p) => p.changed);
        const changedBefore = changed ? vm.ss?.before.find((b) => b.name === changed.name) : undefined;
        const delayed = !!changed && !!changedBefore && changed.claimAge > changedBefore.claimAge;
        vm.blurb = changed
          ? delayed
            ? `${changed.name} delays their claim to ${changed.claimAge}.`
            : `${changed.name}'s claiming strategy is optimized.`
          : 'Optimized claiming ages across the household.';
        vm.strategyTitle = changed
          ? delayed
            ? `Delay ${changed.name}'s Social Security`
            : `Optimize ${changed.name}'s Social Security`
          : 'Time the claims';
        vm.strategyBody = changed
          ? delayed
            ? `${changed.name} claims at ${changed.claimAge} instead of ${changedBefore?.claimAge ?? 67}. Extra years of benefit growth compound over the ${horizon}-year horizon.`
            : `${changed.name}'s claiming is optimized so household benefits compound over the ${horizon}-year horizon.`
          : `Claiming ages are timed so household benefits compound over the ${horizon}-year horizon.`;
        vm.whyBody = 'No tax impact, no reallocation. The least disruptive change with guaranteed upside.';
        if (!vm.ss) vm.framing = 'generic';
      } else if (category === 'investment-timing') {
        vm.buckets = buildBuckets(driver, horizon);
        // Real per-year divergence data (schema v8); the panel falls back to
        // the synthesized curve when absent.
        if (driver.alignmentSeries && driver.alignmentSeries.length > 1) {
          vm.alignmentSeries = driver.alignmentSeries;
        }
        vm.blurb = 'Bucketing assets by time horizon lets long-term money grow while near-term needs stay safe.';
        vm.strategyTitle = "Match each dollar to when you'll need it";
        vm.strategyBody =
          'Assets split into buckets by time horizon, so short-term stays safe while long-term can grow.';
        vm.whyBody =
          'A single flat return leaves long-horizon money too conservative. Bucketing lets distant assets earn more while near-term needs stay protected.';
        if (!vm.buckets) vm.framing = 'generic';
      } else if (category === 'tax') {
        // Real cumulative-tax series + end-of-plan mix (schema v8); the panel
        // falls back to the synthesized curves when absent.
        if (driver.taxSeries && driver.taxSeries.length > 1) {
          vm.taxSeries = driver.taxSeries;
        }
        if (driver.endOfPlanAccountMix) {
          vm.endOfPlanAccountMix = driver.endOfPlanAccountMix;
        }
        if (driver.baselineEndOfPlanAccountMix) {
          vm.baselineEndOfPlanAccountMix = driver.baselineEndOfPlanAccountMix;
        }
        const roth = summarizeRoth(driver.rothConversions);
        if (roth) {
          roth.taxSavings = driver.taxSavings || 0;
          vm.roth = roth;
          const years = roth.lastYear && roth.firstYear ? roth.lastYear - roth.firstYear + 1 : roth.schedule.length;
          vm.blurb = `${fmtMoney(roth.total)} over ${years} year${years === 1 ? '' : 's'}${
            roth.taxSavings > 0 ? ` saves ${fmtMoney(roth.taxSavings)} in taxes` : ''
          }.`;
          vm.strategyTitle = `Move ${fmtMoney(roth.total)} to Tax-Free`;
          vm.strategyBody = `Converted from tax-deferred over ${years} year${years === 1 ? '' : 's'}, staying within your bracket.`;
        } else {
          vm.framing = 'generic';
          vm.blurb = 'A tax-efficiency strategy tuned to your plan.';
          vm.strategyTitle = 'Pay tax once, on your terms';
          vm.strategyBody = 'Draw from the right accounts in the right order to keep lifetime taxes low.';
        }
        vm.whyBody =
          'Paying tax now at a known rate beats paying more later. Tax-free assets pass to heirs untouched by income tax.';
      } else {
        vm.blurb = 'A strategy worth a closer look with your advisor.';
        vm.strategyTitle = driver.type || 'Planning strategy';
        vm.strategyBody = `This strategy adds ${value} of projected value to your plan.`;
        vm.whyBody = 'Worth a conversation — your advisor can walk through the mechanics.';
      }

      return vm;
    });
}

/* ─────────────────────────────── act VMs ─────────────────────────────── */

export interface OverviewVM {
  planYear: number;
  clientNames: string;
  planDateLabel: string | null;
  preparedBy: string | null;
  lifestyle: { value: string; sub: string } | null;
  legacy: { value: string; sub: string } | null;
  totalPlanValue: { proposed: string; sub: string } | null;
}

export function buildOverviewVM(
  payload: WebReportPayload,
  enrichment: ReportEnrichment | null,
  advisor: AdvisorMeta | null
): OverviewVM {
  const planDate = enrichment?.planDate ? new Date(enrichment.planDate) : null;
  const m = enrichment?.metrics;
  const target = enrichment?.household.targetIncome ?? payload.client.targetSpend;

  let legacy: OverviewVM['legacy'] = null;
  if (m?.starting?.legacyAfterTax != null && m?.optimized?.legacyAfterTax != null) {
    const delta = m.optimized.legacyAfterTax - m.starting.legacyAfterTax;
    if (delta > 0) legacy = { value: `${fmtMoney(delta)}+`, sub: 'more left to heirs' };
  } else if (payload.hero.legacyAtCeiling > 0 && enrichment?.showLifestyle !== false) {
    // Ceiling-based fallback is spend-more framing — suppressed when the advisor
    // opted the lifestyle presentation out for this run.
    legacy = { value: fmtMoney(payload.hero.legacyAtCeiling), sub: 'potential legacy' };
  }

  return {
    planYear: planDate ? planDate.getFullYear() : new Date().getFullYear(),
    clientNames: payload.client.firstNames?.join(' & ') || payload.client.displayName,
    planDateLabel: planDate
      ? planDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      : null,
    preparedBy: advisor?.name || null,
    lifestyle: target ? { value: `${fmtMoneyFull(target)}/yr`, sub: 'annual cashflow (after-tax)' } : null,
    // Subtext states the metric's composition rather than the "vs SC01" delta —
    // that comparison is dominated by return-model differences and reads as
    // overstated without context. Terms match the Details act stat labels.
    totalPlanValue: m?.optimized
      ? {
          proposed: fmtMoney(m.optimized.totalPlanValue),
          sub: 'After-Tax Cashflow + Savings at End',
        }
      : null,
    legacy,
  };
}

export interface DisclosuresVM {
  /** "{clients} · {year} Financial Plan" — plan identification for the footer. */
  planLine: string;
  /** "Plan date {MM/DD/YYYY} · Prepared by {advisor}, {company}" — fragments omitted when unknown. */
  metaLine: string;
}

export function buildDisclosuresVM(
  payload: WebReportPayload,
  enrichment: ReportEnrichment | null,
  advisor: AdvisorMeta | null
): DisclosuresVM {
  const planDate = enrichment?.planDate ? new Date(enrichment.planDate) : null;
  const clientNames = payload.client.firstNames?.join(' & ') || payload.client.displayName;
  const planYear = planDate ? planDate.getFullYear() : new Date().getFullYear();

  const preparedBy = [advisor?.name, advisor?.company].filter(Boolean).join(', ');
  const metaLine = [
    planDate
      ? `Plan date ${planDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`
      : null,
    preparedBy ? `Prepared by ${preparedBy}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    planLine: clientNames ? `${clientNames} · ${planYear} Financial Plan` : `${planYear} Financial Plan`,
    metaLine,
  };
}

export interface DetailsRowVM {
  label: string;
  /** Display string; '—' when absent. */
  display: string;
  raw: number | null;
  /** Canonical path when editable; null = read-only row. */
  path: string | null;
  note: string | null;
  estimated: boolean;
}

export interface DetailsPersonVM {
  name: string;
  age: number | null;
  status: string;
  /** Editable retirement age — surfaced in the header (in place of the status
   *  pill) when editing, rather than as a value row. */
  retireAge: DetailsRowVM;
  rows: DetailsRowVM[];
}

export interface DetailsVM {
  targetIncome: DetailsRowVM;
  savings: {
    total: number;
    buckets: {
      label: string;
      amount: number;
      pct: number;
      colorKey: 'teal' | 'blue' | 'navy';
      /** Editable path for the client-update flow (null = read-only bucket). */
      path: string | null;
    }[];
    estimated: boolean;
  } | null;
  people: DetailsPersonVM[];
  startingMetrics: {
    horizon: string;
    afterTaxIncome: string;
    savingsAtEnd: string;
    totalPlanValue: string;
  } | null;
  anyEstimates: boolean;
}

const perYear = (n: number | null) => (n == null ? '—' : `${fmtMoneyFull(n)}/yr`);

export function buildDetailsVM(
  payload: WebReportPayload,
  enrichment: ReportEnrichment | null
): DetailsVM {
  const target = enrichment?.household.targetIncome ?? payload.client.targetSpend ?? null;
  const targetIncome: DetailsRowVM = {
    label: 'Annual Cashflow (After-Tax)',
    display: target != null ? `${fmtMoneyFull(target)} / year` : '—',
    raw: target,
    path: 'targetIncome',
    note: null,
    estimated: !!enrichment?.household.targetIncomeEstimated,
  };

  let savings: DetailsVM['savings'] = null;
  const s = enrichment?.household.savings;
  if (s && s.total > 0) {
    const pct = (n: number) => (s.total > 0 ? Math.round((n / s.total) * 100) : 0);
    // Keep all three buckets (even $0) with editable paths so the client can
    // correct or add to any of them; the viewer hides $0 bars in read mode.
    savings = {
      total: s.total,
      buckets: [
        { label: 'Taxable', amount: s.taxable, pct: pct(s.taxable), colorKey: 'teal' as const, path: 'savings.taxable' },
        { label: 'Tax-Deferred', amount: s.taxDeferred, pct: pct(s.taxDeferred), colorKey: 'blue' as const, path: 'savings.taxDeferred' },
        { label: 'Tax-Free', amount: s.taxFree, pct: pct(s.taxFree), colorKey: 'navy' as const, path: 'savings.taxFree' },
      ],
      estimated: s.estimated,
    };
  } else if (payload.client.totalSavings > 0) {
    savings = { total: payload.client.totalSavings, buckets: [], estimated: false };
  }

  const people: DetailsPersonVM[] = (enrichment?.people || []).map((p) => {
    const status = p.retired
      ? 'Retired'
      : p.retireAge
        ? `Retires at ${p.retireAge}${p.retireAgeEstimated ? '*' : ''}`
        : 'Working';
    const who = p.wages.path.split('.')[0]; // 'client1' | 'client2'
    const retireAge: DetailsRowVM = {
      label: 'Retirement age',
      display: p.retireAge != null ? String(p.retireAge) : '—',
      raw: p.retireAge,
      path: `${who}.retirementAge`,
      note: null,
      estimated: p.retireAgeEstimated,
    };
    const rows: DetailsRowVM[] = [
      {
        // Always editable — a "retired" person can correct part-time wages or,
        // together with the editable retirement age, fix a wrong working state.
        label: 'Wages',
        display: perYear(p.wages.amount),
        raw: p.wages.amount,
        path: p.wages.path,
        note: p.retireAge && !p.retired ? 'until retirement' : null,
        estimated: p.wages.estimated,
      },
      {
        label: 'Social Security',
        display: perYear(p.socialSecurity.amount),
        raw: p.socialSecurity.amount,
        path: p.socialSecurity.path,
        note: p.socialSecurity.claimingNow ? 'claiming now' : 'projected benefit',
        estimated: p.socialSecurity.estimated,
      },
      {
        label: 'Pension',
        display: p.pension ? perYear(p.pension.amount) : '—',
        raw: p.pension?.amount ?? null,
        path: p.pension ? p.pension.path : `${p.wages.path.split('.')[0]}.pensionAmount`,
        note: p.pension?.startAge ? `begins at ${p.pension.startAge}` : null,
        estimated: false,
      },
    ];
    return { name: p.firstName || 'Client', age: p.age, status, retireAge, rows };
  });

  const m = enrichment?.metrics.starting;
  return {
    targetIncome,
    savings,
    people,
    startingMetrics: m
      ? {
          horizon: `${payload.client.planHorizonYears} yrs`,
          afterTaxIncome: `${fmtMoney(m.afterTaxIncome)}*`,
          savingsAtEnd: `${fmtMoney(m.endOfPlanAssets)}*`,
          totalPlanValue: `${fmtMoney(m.totalPlanValue)}*`,
        }
      : null,
    anyEstimates:
      !!enrichment &&
      (enrichment.household.targetIncomeEstimated ||
        enrichment.household.savings.estimated ||
        enrichment.people.some(
          (p) => p.wages.estimated || p.socialSecurity.estimated || p.retireAgeEstimated
        )),
  };
}

export interface ResultsVM {
  totalPlanValue: string | null;
  addedChip: string | null;
  heirs: { after: string; delta: string; sub: string } | null;
  changes: { label: string; before: string; after: string; improved: boolean }[];
  optimizationTitles: string[];
  /** Advisor's per-run choice: false hides the "room to spend more" framing. */
  showLifestyle: boolean;
}

export function buildResultsVM(
  enrichment: ReportEnrichment | null,
  optimizations: OptimizationVM[]
): ResultsVM {
  const m = enrichment?.metrics;
  const changes: ResultsVM['changes'] = [];
  let heirs: ResultsVM['heirs'] = null;
  let addedChip: string | null = null;

  if (m?.starting && m?.optimized) {
    const added = m.optimized.totalPlanValue - m.starting.totalPlanValue;
    if (added > 0) addedChip = `+${fmtMoney(added)} added · up from ${fmtMoney(m.starting.totalPlanValue)}`;
    changes.push({
      label: 'After-Tax Cashflow',
      before: fmtMoney(m.starting.afterTaxIncome),
      after: fmtMoney(m.optimized.afterTaxIncome),
      improved: m.optimized.afterTaxIncome > m.starting.afterTaxIncome,
    });
    changes.push({
      label: 'Savings at End',
      before: fmtMoney(m.starting.endOfPlanAssets),
      after: fmtMoney(m.optimized.endOfPlanAssets),
      improved: m.optimized.endOfPlanAssets > m.starting.endOfPlanAssets,
    });
    if (m.starting.totalTaxes != null && m.optimized.totalTaxes != null) {
      changes.push({
        label: 'Total Taxes Paid',
        before: fmtMoney(m.starting.totalTaxes),
        after: fmtMoney(m.optimized.totalTaxes),
        improved: false,
      });
    }
    if (m.starting.legacyAfterTax != null && m.optimized.legacyAfterTax != null) {
      const delta = m.optimized.legacyAfterTax - m.starting.legacyAfterTax;
      heirs = {
        after: fmtMoney(m.optimized.legacyAfterTax),
        delta: `+${fmtMoney(delta)}`,
        sub: `growing from ${fmtMoney(m.starting.legacyAfterTax)} to ${fmtMoney(m.optimized.legacyAfterTax)}`,
      };
    }
  }

  return {
    totalPlanValue: m?.optimized ? fmtMoney(m.optimized.totalPlanValue) : null,
    addedChip,
    heirs,
    changes,
    optimizationTitles: optimizations.map((o) => o.title),
    showLifestyle: enrichment?.showLifestyle !== false,
  };
}

export interface NextStepVM {
  when: 'Now' | 'Future';
  title: string;
  body: string;
}

export function buildNextStepsVM(optimizations: OptimizationVM[]): NextStepVM[] {
  const steps: NextStepVM[] = [];
  for (const o of optimizations) {
    if (o.category === 'investment-timing') {
      const parts = (o.buckets || [])
        .map((b) => `${b.name} (${fmtMoney(b.amount)})`)
        .join(', ');
      steps.push({
        when: 'Now',
        title: 'Align Investments',
        body: parts ? `Separate into ${parts}.` : 'Bucket assets by when you will need them.',
      });
    }
    if (o.category === 'tax' && o.roth) {
      const years =
        o.roth.firstYear && o.roth.lastYear && o.roth.lastYear > o.roth.firstYear
          ? ` (years ${o.roth.firstYear}–${o.roth.lastYear})`
          : '';
      steps.push({
        when: 'Now',
        title: 'Roth Conversions',
        body: `Convert ${fmtMoney(o.roth.total)} over ${o.roth.schedule.length} year${o.roth.schedule.length === 1 ? '' : 's'}${years}, staying within your bracket.`,
      });
    }
  }
  for (const o of optimizations) {
    if (o.category === 'social-security') {
      const changed = o.ss?.after.find((p) => p.changed);
      steps.push({
        when: 'Future',
        title: 'Social Security',
        body: changed
          ? `${changed.name} delays to age ${changed.claimAge}, adding ${o.valueAddedLabel} of lifetime value.`
          : `Optimized claiming adds ${o.valueAddedLabel} of lifetime value.`,
      });
    }
    if (o.category === 'legacy') {
      steps.push({ when: 'Future', title: 'Legacy', body: `This strategy adds ${o.valueAddedLabel} for your heirs.` });
    }
  }
  return steps;
}

export interface CloseVM {
  commitments: { title: string; value: string; sub: string }[];
  preparedByLine: string | null;
}

export function buildCloseVM(
  payload: WebReportPayload,
  enrichment: ReportEnrichment | null,
  advisor: AdvisorMeta | null,
  optimizations: OptimizationVM[]
): CloseVM {
  const commitments: CloseVM['commitments'] = [];
  const target = enrichment?.household.targetIncome ?? payload.client.targetSpend;
  if (target) {
    commitments.push({
      title: 'Annual Cashflow',
      value: `${fmtMoneyFull(target)}/yr`,
      sub: `after-tax, for ${payload.client.planHorizonYears} years`,
    });
  }
  const taxOpt = optimizations.find((o) => o.category === 'tax');
  if (taxOpt?.roth?.taxSavings) {
    commitments.push({ title: 'Manage Taxes', value: fmtMoney(taxOpt.roth.taxSavings), sub: 'saved over the plan' });
  }
  const legacy = enrichment?.metrics.optimized?.legacyAfterTax;
  if (legacy != null && legacy > 0) {
    commitments.push({ title: 'Leave a Legacy', value: fmtMoney(legacy), sub: 'to your heirs' });
  }

  const parts = [advisor?.name, advisor?.company].filter(Boolean);
  return {
    commitments,
    preparedByLine: parts.length > 0 ? `Prepared by ${parts.join(' · ')}` : null,
  };
}

/* ─────────────────────────────── CTA ─────────────────────────────── */

export interface DeckCTA {
  bookLabel: string;
  bookUrl?: string;
  rerunUrl?: string;
}

export function buildCTA(advisor: AdvisorMeta | null): DeckCTA {
  const advisorFirst = (advisor?.name || '').split(/\s+/)[0] || '';
  const interviewBase = (import.meta.env.VITE_CLIENT_INTERVIEW_URL || '').replace(/\/$/, '');
  return {
    bookLabel: advisorFirst ? `Book a follow-up with ${advisorFirst}` : 'Book a follow-up',
    bookUrl: advisor?.branding?.calendlyUrl,
    rerunUrl:
      interviewBase && advisor?.customerId
        ? `${interviewBase}/wizard/${advisor.customerId}`
        : undefined,
  };
}
